import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { CustomCard, CustomCardTitle, Button } from "@repo/ui";
import LazyLoadImage from "../../../../components/LazyLoadImage/LazyLoadImage";
import withAnimation from "../../../../hoc/withCardAnimation/withCardAnimation";
import { GiftTableProduct } from "../../../../redux/slices/giftTableSlice";
import { Product, getProduct } from "../../../../services/productApiService";
import placeholderImage from "../../../../assets/placeholders/picture.webp";
import { FaCartShopping, FaArrowRightLong } from "react-icons/fa6";
import { MdStars } from "react-icons/md";
import Tag from "../../../../components/Tag/Tag";
import { useNavigate } from "react-router-dom";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";

const LOCALE = "modules_Guests_components_TableContent_LazyProductCard";

interface LazyProductCardProps {
  product: GiftTableProduct;
  onProductSelect: (product: { local: GiftTableProduct; api: Product }) => void;
  getCartQuantity: (productID: string) => number;
  giftTableID: string;
}

const AnimatedCard = withAnimation(CustomCard);

const LazyProductCard: React.FC<LazyProductCardProps> = ({
  product,
  onProductSelect,
  getCartQuantity,
  giftTableID,
}) => {
  const { t } = useTranslation(LOCALE);
  const [isVisible, setIsVisible] = useState(false);
  const [productData, setProductData] = useState<Product | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const HandleSeeCart = () => {
    navigate(`/guests/gifttable/cart/${giftTableID}`);
  };

  const fetchProductData = useCallback(async () => {
    const data = await getProduct(product.productID);
    setProductData(data);
  }, [product.productID]);

  const onIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    if (entry.isIntersecting) {
      setIsVisible(true);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(onIntersection, {
      rootMargin: "100px",
      threshold: 0.1,
    });

    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [onIntersection]);

  useEffect(() => {
    if (isVisible && !productData) {
      fetchProductData();
    }
  }, [isVisible, productData, fetchProductData]);

  const handleClick = () => {
    if (productData) {
      onProductSelect({ local: product, api: productData });
    }
  };

  const cartQuantity = getCartQuantity(product.productID);
  const availableQuantity = Math.max(
    0,
    (product.quantity ?? 0) - (product.taken ?? 0) - cartQuantity
  );

  const notAvailableMessage =
    cartQuantity > 0 ? t("notAvailableInCart") : t("notAvailable");

  return (
    <div
      ref={cardRef}
      className="relative group cursor-pointer"
      onClick={handleClick}
    >
      <AnimatedCard className="relative rounded-s shadow-none border transition-transform transform group-hover:scale-102 group-hover:shadow-lg">
        {cartQuantity > 0 && (
          <Tag>
            <FaCartShopping className="w-4 h-4" />
            <span>{cartQuantity}</span>
          </Tag>
        )}
        {product.priority && (
          <MdStars className="absolute top-2 right-2 h-6 w-6 text-secondary" />
        )}
        <LazyLoadImage
          src={productData?.pictures?.[0]?.trim() || placeholderImage}
          alt={productData?.title || t("loading")}
          className="w-full h-64 md:h-96 object-contain"
        />
        <div className="p-4">
          {productData ? (
            <>
              <p className="text-lg font-bold text-primary">
                {productData.brand}
              </p>
              <CustomCardTitle>{productData.title}</CustomCardTitle>
              <p className="text-lg font-semibold text-primary mt-2">{`${(productData.display_total_charge ?? productData.display_price)?.toLocaleString(
                "es-MX",
                { style: "currency", currency: productData.display_currency }
              )}`}</p>
              <div className="text-sm text-gray-600 mt-2 flex items-center justify-between">
                <span>
                  {t("availableToGift")}:{" "}
                  <span className="font-bold">{availableQuantity}</span>
                </span>
                {availableQuantity && cartQuantity > 0 && (
                  <Button
                    className="flex items-center hover:underline ml-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      HandleSeeCart();
                    }}
                  >
                    {t("continueShopping")}
                  </Button>
                )}
              </div>
            </>
          ) : (
            <p className="text-gray-400 text-sm">{t("loading")}</p>
          )}
        </div>
        {availableQuantity === 0 && (
          <div className="absolute inset-0 bg-white/75 flex flex-col items-center justify-center p-4">
            <p className="text-center font-bold text-lg text-primary">
              {notAvailableMessage}
            </p>
            {cartQuantity > 0 && (
              <Button
                label={t("seeCart")}
                className="mt-4 bg-primary text-white px-4 py-2 rounded flex items-center justify-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  HandleSeeCart();
                }}
              >
                {t("continueShopping")}{" "}
                <FaArrowRightLong className="ml-2 w-4 h-4" />
              </Button>
            )}
          </div>
        )}
      </AnimatedCard>
    </div>
  );
};

export default withTranslations(LOCALE)(LazyProductCard);
